import React from 'react'
import { Box, Typography, Divider } from '@mui/material'

interface Product {
  product_id: number
  product_name: string
  product_image_url: string
  amount_display: string | null
  container_id: number
  container_slot_number: number
  container_type: string
  replenishment_order_id: number | null
}

interface QueuedOrder {
  queued_order_id: number
  queued_order_state: string
  products: Product[]
}

interface OrderItem {
  recipe: {
    id: number
    name: string
  }
  amount: number
  queued_orders: QueuedOrder[]
}

export interface OrderLineage {
  [orderItemId: string]: OrderItem
}

interface OrderItemsDetailsProps {
  orderLineage: OrderLineage | null
  loading?: boolean
}

const OrderItemsDetails = ({ orderLineage, loading = false }: OrderItemsDetailsProps) => {
  if (loading) {
    return <Typography variant='body2' color='text.secondary'>Loading product history...</Typography>
  }

  if (!orderLineage || Object.keys(orderLineage).length === 0) {
    return <Typography variant='body2' color='text.secondary'>No product history available.</Typography>
  }

  const entries = Object.entries(orderLineage)

  return (
    <Box className='order-items-details'>
      {entries.map(([orderItemId, itemData], itemIndex) => (
        <Box key={orderItemId} className='order-item-block'>
          {itemIndex > 0 && <Divider className='order-item-divider' />}
          {itemData.queued_orders.map((queuedOrder) => (
            <Box key={queuedOrder.queued_order_id} className='queued-order-section'>
              {queuedOrder.products.length === 0 ? (
                <Typography variant='body2' color='text.secondary'>
                  No ingredients added for this meal.
                </Typography>
              ) : (
                <Box className='products-list'>
                  {queuedOrder.products.map((product, productIndex) => (
                    <Box key={`${product.product_id}-${product.container_id}-${productIndex}`} className='product-item'>
                      <img
                        src={product.product_image_url}
                        alt={product.product_name}
                        className='product-image'
                      />
                      <Box className='product-content'>
                        <Typography variant='body1' className='product-name'>
                          {product.product_name}
                        </Typography>
                        <Typography variant='body2' className='container-info'>
                          <strong>{product.amount_display || '—'}</strong>
                          {' from container '}
                          <strong>#{product.container_slot_number}</strong>
                          {' (ID '}<strong>{product.container_id}</strong>{')'}
                        </Typography>
                        {product.replenishment_order_id ? (
                          <a
                            href={`/replenishment_orders/${product.replenishment_order_id}`}
                            className='replenishment-link'
                          >
                            View replenishment order
                          </a>
                        ) : (
                          <Typography variant='caption' className='no-replenishment-data'>
                            No replenishment data
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  )
}

export default OrderItemsDetails
